import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsObject,
  IsOptional,
  IsString,
  Length,
  Matches,
  MaxLength,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../../../../@base/base.dto';
import { Country, FrenchDepartment, FrenchRegion } from '../../../../administrative';
import { OpeningHoursDayDto, OpeningHoursDto } from '../../../../attributes';
import { AgentProfileId, FileId, UserId } from '../../../../types/etxe-branded-object-ids.type';
import { UserAvatarFileDto } from '../../../file/user-avatar-file-dto';
import { AgentCardDto } from '../agent-card/agent-card.dto';

import { UserResponsivenessDto } from '../../../users/user-responsiveness.dto';

export type AgentType = 'titulaire' | 'mandataire';

export class AgentUserIdentityPublicDto {
  @IsString()
  @IsOptional()
  firstName?: string;

  @IsString()
  @IsOptional()
  lastName?: string;

  @ValidateNested()
  @Type(() => UserAvatarFileDto)
  @IsOptional()
  avatar?: UserAvatarFileDto;
}

export class AgentUserPublicDto {
  @ValidateNested()
  @Type(() => AgentUserIdentityPublicDto)
  @IsOptional()
  identity?: AgentUserIdentityPublicDto;

  @ValidateNested()
  @Type(() => UserResponsivenessDto)
  @IsOptional()
  responsiveness?: UserResponsivenessDto;

  @IsDate()
  @IsOptional()
  createdAt?: Date;
}

export enum AgentSpecialty {
  APARTMENT = 'apartment',
  HOUSE = 'house',
  PRESTIGE = 'prestige',
  BUILDING = 'building',
  LAND = 'land',
  COMMERCIAL = 'commercial',
}

export enum AgentServiceOffering {
  PHOTO_PRO = 'photo_pro',
  VIRTUAL_TOUR = 'virtual_tour',
  DRONE = 'drone',
  HOME_STAGING = 'home_staging',
  FINANCIAL_SCREENING = 'financial_screening',
  REPORT_24H = 'report_24h',
  PREMIUM_PORTALS = 'premium_portals',
  NOTARY_SUPPORT = 'notary_support',
}

export const KNOWN_REAL_ESTATE_NETWORKS = [
  'IAD France',
  'Safti',
  'Propriétés Privées',
  'Capifrance',
  'BSK Immobilier',
  'MegAgence',
  'Dr House Immo',
  'Optimhome',
  'Orpi',
  'Century 21',
  'Laforêt',
  'Stéphane Plaza Immobilier',
  'Guy Hoquet',
  'ERA Immobilier',
  'Arthurimmo',
  'Nexity',
  'Agence Indépendante',
  'Autre',
] as const;

export class PreferredCityDto {
  @IsString()
  @IsNotEmpty()
  name!: string;

  @IsString()
  @IsNotEmpty()
  zipCode!: string;

  @IsString()
  @IsOptional()
  departmentCode?: string;

  @IsString()
  @IsOptional()
  inseeCode?: string;

  @IsString()
  @IsOptional()
  label?: string;
}

export class AgentSocialLinksDto {
  @IsString()
  @IsOptional()
  website?: string;

  @IsString()
  @IsOptional()
  googleReviewUrl?: string;

  @IsString()
  @IsOptional()
  opinionSystemUrl?: string;

  @IsString()
  @IsOptional()
  linkedin?: string;
}

export class AgentOfficeAddressDto {
  @IsString()
  @IsOptional()
  streetNumber?: string;

  @IsString()
  @IsOptional()
  streetType?: string;

  @IsString()
  @IsOptional()
  streetName?: string;

  @IsString()
  @IsOptional()
  zipCode?: string;

  @IsString()
  @IsOptional()
  city?: string;

  @IsString()
  @IsOptional()
  departmentCode?: string;

  @IsString()
  @IsOptional()
  complement?: string;
}

export type AgentOpeningHoursDayDto = OpeningHoursDayDto;
export const AgentOpeningHoursDayDto = OpeningHoursDayDto;
export type AgentOpeningHoursDto = OpeningHoursDto;
export const AgentOpeningHoursDto = OpeningHoursDto;

export class InterventionZoneDto {
  @IsEnum(Country)
  @IsNotEmpty()
  country!: Country;

  @IsArray()
  @IsEnum(FrenchDepartment, { each: true })
  @IsOptional()
  departments?: FrenchDepartment[];
}

export class ProfessionalIdentityDto {
  @IsString()
  @Length(14, 14)
  @Matches(/^[0-9]{14}$/, { message: 'SIRET must be 14 digits' })
  @IsNotEmpty()
  siret!: string;

  @IsString()
  @IsNotEmpty()
  companyName!: string;

  @IsString()
  @IsOptional()
  carteTNumber?: string;

  @IsOptional()
  cci?: {
    id: string;
    name: string;
    president?: {
      firstName: string;
      lastName: string;
    };
  };

  @IsString()
  @IsOptional()
  cardExpiresAt?: string;

  @IsString()
  @IsOptional()
  professionalFileId?: FileId;
}

export class AgentVisibilitySettingsDto {
  @IsBoolean()
  @IsOptional()
  isPublic?: boolean;

  @IsBoolean()
  @IsOptional()
  isVacationMode?: boolean;

  @IsBoolean()
  @IsOptional()
  allowSearchIndexing?: boolean;
}

export class AgentContactInfosDto {
  @IsString()
  @IsOptional()
  email?: string;

  @IsBoolean()
  @IsOptional()
  showEmail?: boolean;

  @IsString()
  @IsOptional()
  mobileNumber?: string;

  @IsBoolean()
  @IsOptional()
  showMobileNumber?: boolean;

  @IsBoolean()
  @IsOptional()
  isMasked?: boolean;
}

export class AgentNotificationSettingsDto {
  @IsBoolean()
  @IsOptional()
  emailAlertsEnabled?: boolean;

  @IsNumber()
  @IsOptional()
  minPrice?: number;

  @IsNumber()
  @IsOptional()
  maxPrice?: number;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  mandateTypes?: string[];

  @IsBoolean()
  @IsOptional()
  creditLowAlertEnabled?: boolean;

  @IsNumber()
  @IsOptional()
  creditThreshold?: number;
}

export class AgentVisibilityUpdateDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentVisibilitySettingsDto)
  visibility!: AgentVisibilitySettingsDto;
}

export class AgentNotificationsUpdateDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentNotificationSettingsDto)
  notifications!: AgentNotificationSettingsDto;
}

export class AgentCardsDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentCardDto)
  @IsOptional()
  activeCard?: AgentCardDto;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => AgentCardDto)
  @IsOptional()
  expiredCards?: AgentCardDto[];
}

export class AgentInterventionDto {
  @IsEnum(FrenchRegion)
  @IsOptional()
  primaryRegion?: FrenchRegion;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => InterventionZoneDto)
  @IsOptional()
  zones?: InterventionZoneDto[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PreferredCityDto)
  @IsOptional()
  preferredCities?: PreferredCityDto[];
}

export class AgentActivityDto {
  @IsString()
  @IsOptional()
  network?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  specialties?: (AgentSpecialty | string)[];

  @IsString()
  @MaxLength(1000)
  @IsOptional()
  bio?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  services?: (AgentServiceOffering | string)[];

  @IsObject()
  @ValidateNested()
  @Type(() => AgentSocialLinksDto)
  @IsOptional()
  socialLinks?: AgentSocialLinksDto;

  @IsString()
  @IsOptional()
  feeScaleUrl?: string;

  @IsNumber()
  @IsOptional()
  averageCommissionRate?: number;
}

export class AgentWorkDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentOfficeAddressDto)
  @IsOptional()
  officeAddress?: AgentOfficeAddressDto;

  @IsObject()
  @ValidateNested()
  @Type(() => OpeningHoursDto)
  @IsOptional()
  openingHours?: OpeningHoursDto;
}

export class AgentSettingsDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentContactInfosDto)
  @IsOptional()
  contactInfos?: AgentContactInfosDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentVisibilitySettingsDto)
  @IsOptional()
  visibility?: AgentVisibilitySettingsDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentNotificationSettingsDto)
  @IsOptional()
  notifications?: AgentNotificationSettingsDto;
}

export class AgentProfileDto extends BaseDto<AgentProfileId> {
  @IsString()
  @IsOptional()
  id?: string;

  @IsBoolean()
  @IsOptional()
  isVacationMode?: boolean;

  user!: UserId | AgentUserPublicDto;

  @IsEnum(['titulaire', 'mandataire'])
  @IsNotEmpty()
  type!: AgentType;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentCardsDto)
  @IsOptional()
  cards?: AgentCardsDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentInterventionDto)
  @IsOptional()
  intervention?: AgentInterventionDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentActivityDto)
  @IsOptional()
  activity?: AgentActivityDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentWorkDto)
  @IsOptional()
  work?: AgentWorkDto;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentSettingsDto)
  @IsOptional()
  settings?: AgentSettingsDto;
}

export class AgentProfileCreationDto {
  @IsEnum(['titulaire', 'mandataire'])
  @IsNotEmpty()
  type!: AgentType;

  @IsString()
  @IsNotEmpty()
  agentCardNumber!: string;

  @IsEnum(FrenchRegion)
  @IsOptional()
  primaryRegion?: FrenchRegion;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => InterventionZoneDto)
  @IsOptional()
  interventionZones?: InterventionZoneDto[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PreferredCityDto)
  @IsOptional()
  preferredCities?: PreferredCityDto[];

  @IsString()
  @IsOptional()
  network?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  specialties?: (AgentSpecialty | string)[];

  @IsString()
  @MaxLength(1000)
  @IsOptional()
  bio?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  services?: (AgentServiceOffering | string)[];

  @IsNumber()
  @IsOptional()
  averageCommissionRate?: number;

  @IsOptional()
  @ValidateNested()
  @Type(() => AgentSocialLinksDto)
  socialLinks?: AgentSocialLinksDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => AgentOfficeAddressDto)
  officeAddress?: AgentOfficeAddressDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => OpeningHoursDto)
  openingHours?: OpeningHoursDto;
}

export class AgentProfileUpdateDto {
  @IsEnum(FrenchRegion)
  @IsOptional()
  primaryRegion?: FrenchRegion;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => InterventionZoneDto)
  @IsOptional()
  interventionZones?: InterventionZoneDto[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PreferredCityDto)
  @IsOptional()
  preferredCities?: PreferredCityDto[];

  @IsString()
  @IsOptional()
  network?: string;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  specialties?: (AgentSpecialty | string)[];

  @IsString()
  @MaxLength(1000)
  @IsOptional()
  bio?: string;

  @IsString()
  @IsOptional()
  feeScaleUrl?: string;

  @IsNumber()
  @IsOptional()
  averageCommissionRate?: number;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  services?: (AgentServiceOffering | string)[];

  @IsNumber()
  @IsOptional()
  experienceYears?: number;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentSocialLinksDto)
  @IsOptional()
  socialLinks?: AgentSocialLinksDto;
}

export class AgentNetworkUpdateDto {
  @IsString()
  @IsOptional()
  network?: string;
}

export class AgentSpecialtiesUpdateDto {
  @IsArray()
  @IsString({ each: true })
  specialties!: (AgentSpecialty | string)[];
}

export class AgentZonesUpdateDto {
  @IsEnum(FrenchRegion)
  @IsOptional()
  primaryRegion?: FrenchRegion;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => InterventionZoneDto)
  interventionZones!: InterventionZoneDto[];
}

export class AgentPreferredCitiesUpdateDto {
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PreferredCityDto)
  preferredCities!: PreferredCityDto[];
}

export class AgentBioUpdateDto {
  @IsString()
  @MaxLength(1000)
  @IsOptional()
  bio?: string;
}

export class AgentCommercialUpdateDto {
  @IsString()
  @IsOptional()
  feeScaleUrl?: string;

  @IsNumber()
  @IsOptional()
  averageCommissionRate?: number;

  @IsArray()
  @IsString({ each: true })
  @IsOptional()
  services?: (AgentServiceOffering | string)[];

  @IsNumber()
  @IsOptional()
  experienceYears?: number;

  @IsObject()
  @ValidateNested()
  @Type(() => AgentSocialLinksDto)
  @IsOptional()
  socialLinks?: AgentSocialLinksDto;
}

export class AgentSocialLinksUpdateDto {
  @IsObject()
  @ValidateNested()
  @Type(() => AgentSocialLinksDto)
  socialLinks!: AgentSocialLinksDto;
}

export class AgentOfficeHoursUpdateDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => AgentOfficeAddressDto)
  officeAddress?: AgentOfficeAddressDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => OpeningHoursDto)
  openingHours?: OpeningHoursDto;
}

export class AgentContactInfosUpdateDto {
  @IsOptional()
  @ValidateNested()
  @Type(() => AgentContactInfosDto)
  contactInfos?: AgentContactInfosDto;
}
